import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Header from "../components/app/Header";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import MagicLinkForm from "../components/app/MagicLinkForm";
import { useSeo } from "../lib/useSeo";

// Only allow internal paths in the post-login redirect.
function safeNext(next) {
  if (!next || typeof next !== "string") return "/browse";
  if (!next.startsWith("/") || next.startsWith("//")) return "/browse";
  return next;
}

export default function Login() {
  const { login } = useAuth();
  const { t } = useTranslation();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const next = safeNext(params.get("next"));
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  // iter222 — tell Google to crawl but not index this utility page so
  // the "Indexed, though blocked by robots.txt" warning clears.
  useSeo({ title: "Log in — CampCompass UK", noindex: true });

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const res = await login(email, password);
    setBusy(false);
    if (res.ok) { toast.success(t("auth.welcomeMessage")); nav(next); }
    else toast.error(res.error);
  };

  const onGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + next;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-[#fafafa]" data-testid="login-page">
      <Header />
      <div className="max-w-md mx-auto px-6 py-16">
        <div className="eyebrow mb-3">{t("auth.membersEyebrow")}</div>
        <h1 className="font-display text-4xl tracking-tight mb-6">{t("auth.welcomeBack")}</h1>

        {/* iter177 — Google button up top for fast returning-visitor auth. */}
        <button
          onClick={onGoogle}
          className="w-full inline-flex items-center justify-center gap-2 bg-white border-2 border-[#0e0e0e] hover:bg-[#fafafa] text-[#0e0e0e] font-bold text-sm px-4 py-3 shadow-[0_4px_0_0_#0e0e0e] hover:translate-y-[1px] hover:shadow-[0_3px_0_0_#0e0e0e] transition-transform mb-4"
          data-testid="google-login"
        >
          <svg className="w-4 h-4" viewBox="0 0 24 24" aria-hidden="true">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          Continue with Google — 1 tap
        </button>

        <div className="mb-4 flex items-center gap-3 text-xs text-[#888]">
          <span className="flex-1 h-px bg-[#ddd]" /> {t("auth.or")} · magic link <span className="flex-1 h-px bg-[#ddd]" />
        </div>

        <MagicLinkForm testId="login-magic" />

        <div className="mb-4 mt-6 flex items-center gap-3 text-xs text-[#888]">
          <span className="flex-1 h-px bg-[#ddd]" /> or use a password <span className="flex-1 h-px bg-[#ddd]" />
        </div>

        <form onSubmit={onSubmit} className="space-y-4 p-6 border hairline bg-white" data-testid="login-form">
          <div>
            <label className="eyebrow block mb-2">{t("auth.email")}</label>
            <input type="email" className="cc-input" value={email} onChange={(e) => setEmail(e.target.value)}
              required data-testid="login-email" />
          </div>
          <div>
            <label className="eyebrow block mb-2">{t("auth.password")}</label>
            <input type="password" className="cc-input" value={password} onChange={(e) => setPassword(e.target.value)}
              required data-testid="login-password" />
          </div>
          <button className="btn-primary w-full flex items-center justify-center gap-2" disabled={busy} data-testid="login-submit">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} {t("auth.logIn")}
          </button>
        </form>

        <div className="my-6 flex items-center gap-3 text-xs text-[#888]">
          <span className="flex-1 h-px bg-[#ddd]" /> new here? <span className="flex-1 h-px bg-[#ddd]" />
        </div>

        <div className="mt-2 text-sm text-[#555] text-center">
          {t("auth.noAccount")} <Link to={`/register${next !== "/browse" ? `?next=${encodeURIComponent(next)}` : ""}`} className="underline text-[#ff4500]" data-testid="to-register">{t("auth.createOne")}</Link>
        </div>
        <div className="mt-4 text-[11px] mono text-[#888]">
          {t("auth.demoCredentials")}
        </div>
      </div>
    </div>
  );
}
